import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { ClipboardCheck, Lock, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";

interface FieldConfig {
  key?: string;
  question?: string;
  type: "number" | "freetext" | "select" | "multi_select" | "consent";
  options?: string[];
  allow_freetext?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  text?: string;
  required?: boolean;
}

interface OnboardingFormProps {
  onComplete: () => void;
}

export function OnboardingForm({ onComplete }: OnboardingFormProps) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [savedResponses, setSavedResponses] = useState<Record<string, unknown> | null>(null);
  const [targetEmotion, setTargetEmotion] = useState<string | null>(null);

  const [config, setConfig] = useState<FieldConfig[]>([]);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [freetextInputs, setFreetextInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    Promise.all([fetchConfig(), checkStatus()]);
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch("/api/onboarding/config", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setConfig(data.config);
    } catch {
      toast.error("Failed to load onboarding config");
    }
  };

  const checkStatus = async () => {
    try {
      const res = await fetch("/api/onboarding/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.completed) {
        setCompleted(true);
        setSavedResponses(data.profile.responses);
        setTargetEmotion(data.profile.target_emotion);
        onComplete();
      }
    } catch {
      toast.error("Failed to check onboarding status");
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (key: string, value: unknown) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const addMultiSelectItem = (key: string, item: string) => {
    const current = (responses[key] as string[] | undefined) || [];
    if (!current.includes(item)) {
      updateResponse(key, [...current, item]);
    }
  };

  const removeMultiSelectItem = (key: string, item: string) => {
    const current = (responses[key] as string[] | undefined) || [];
    updateResponse(key, current.filter(i => i !== item));
  };

  const addFreetextItem = (key: string) => {
    const text = freetextInputs[key]?.trim();
    if (!text) return;
    addMultiSelectItem(key, text);
    setFreetextInputs(prev => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch("/api/onboarding", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ responses }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || "Failed to submit onboarding");
        return;
      }
      toast.success("Onboarding complete!");
      setCompleted(true);
      onComplete();
    } catch {
      toast.error("Failed to submit onboarding");
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = (): boolean => {
    for (const field of config) {
      if (field.type === "consent") {
        if (responses["consent"] !== true) return false;
      } else if (field.key && field.required !== false) {
        const val = responses[field.key];
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
      }
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (completed && savedResponses) {
    return (
      <Card className="max-w-lg mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Onboarding Complete</CardTitle>
          </div>
          <CardDescription>Your profile is locked and cannot be changed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {config.filter(f => f.key && f.type !== "consent").map(field => (
              <React.Fragment key={field.key}>
                <div className="text-muted-foreground">{field.question}</div>
                <div>
                  {Array.isArray(savedResponses[field.key!])
                    ? (savedResponses[field.key!] as string[]).join(", ")
                    : String(savedResponses[field.key!] ?? "—")}
                </div>
              </React.Fragment>
            ))}
            {targetEmotion && (
              <>
                <div className="text-muted-foreground">Target Emotion</div>
                <div><Badge variant="secondary">{targetEmotion.replace(/_/g, " ")}</Badge></div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          <CardTitle>Annotator Onboarding</CardTitle>
        </div>
        <CardDescription>
          Please complete this form before starting annotations. Your profile cannot be changed after submission.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {config.map((field, idx) => {
            if (field.type === "consent") {
              return (
                <div key={idx} className="flex items-start gap-2 pt-2 border-t">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={responses["consent"] === true}
                    onChange={e => updateResponse("consent", e.target.checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="consent" className="text-sm leading-tight">
                    {field.text}
                  </Label>
                </div>
              );
            }

            const key = field.key!;

            if (field.type === "number") {
              return (
                <div key={key} className="space-y-2">
                  <Label htmlFor={key}>{field.question}</Label>
                  <Input
                    id={key}
                    type="number"
                    min={field.min}
                    max={field.max}
                    value={(responses[key] as string) ?? ""}
                    onChange={e => updateResponse(key, e.target.value ? parseInt(e.target.value) : "")}
                    required={field.required !== false}
                    placeholder={field.placeholder}
                  />
                </div>
              );
            }

            if (field.type === "freetext") {
              return (
                <div key={key} className="space-y-2">
                  <Label htmlFor={key}>{field.question}</Label>
                  <Input
                    id={key}
                    value={(responses[key] as string) ?? ""}
                    onChange={e => updateResponse(key, e.target.value)}
                    required={field.required !== false}
                    placeholder={field.placeholder}
                  />
                </div>
              );
            }

            if (field.type === "select") {
              return (
                <div key={key} className="space-y-2">
                  <Label>{field.question}</Label>
                  <Select value={(responses[key] as string) ?? ""} onValueChange={v => updateResponse(key, v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            if (field.type === "multi_select") {
              const selected = (responses[key] as string[] | undefined) || [];
              return (
                <div key={key} className="space-y-2">
                  <Label>{field.question}</Label>
                  <div className="flex flex-wrap gap-2">
                    {field.options?.map(opt => {
                      const isSelected = selected.includes(opt);
                      return (
                        <Badge
                          key={opt}
                          variant={isSelected ? "default" : "outline"}
                          className="cursor-pointer select-none"
                          onClick={() => isSelected ? removeMultiSelectItem(key, opt) : addMultiSelectItem(key, opt)}
                        >
                          {opt}
                          {isSelected && <X className="h-3 w-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                  {field.allow_freetext && (
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Add other..."
                        value={freetextInputs[key] ?? ""}
                        onChange={e => setFreetextInputs(prev => ({ ...prev, [key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFreetextItem(key); } }}
                        className="text-sm"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => addFreetextItem(key)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {selected.filter(s => !field.options?.includes(s)).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selected.filter(s => !field.options?.includes(s)).map(custom => (
                        <Badge key={custom} variant="default" className="cursor-pointer select-none">
                          {custom}
                          <X className="h-3 w-3 ml-1" onClick={() => removeMultiSelectItem(key, custom)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}

          <Button type="submit" className="w-full" disabled={submitting || !isFormValid()}>
            {submitting ? "Submitting..." : "Submit & Start Annotating"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
